import type { CallbackType, ConfigResponse } from '../store/modules/merchant/types';

/** Config names that never inherit to child merchants (aligned with MMS NON_INHERITABLE_CONFIGS). */
export const NON_INHERITABLE_CONFIG_NAMES: readonly ConfigResponse['name'][] = [
  'CLICK_TO_PAY',
  'SURCHARGE_FEE',
  'GENERAL_FEE',
];

export function isInheritableConfigName(name: ConfigResponse['name']): boolean {
  return !NON_INHERITABLE_CONFIG_NAMES.includes(name);
}

/** Portal-facing labels for save bar / banner copy; align with section titles on each settings page. */
const CONFIG_DISPLAY_NAMES: Partial<Record<ConfigResponse['name'], string>> = {
  ADDITIONAL_TRANSACTION_DATA: 'Additional Transaction Data',
  AGENTIC_CHECKOUT: 'Agentic Checkout',
  AVS_DECLINE: 'AVS Decline',
  CO_BRANDING: 'Co-branding',
  DISALLOW_CART_EDITS: 'Cart edits',
  DISCOUNTS: 'Discounts',
  FLEX_PAY: 'Flex Pay',
  GENERAL_FEE: 'General fee',
  IS_PAYMENT_PLATFORM: 'Payment Platform',
  MERCHANT_BRANDING: 'Branding',
  NON_ACCEPTED_CARD_BRANDS: 'Card Acceptance',
  NO_SHIPPING_REQUIRED: 'Shipping required',
  PARTIAL_AUTHORIZATIONS: 'Partial Authorization',
  PARTIAL_PAYMENTS: 'Partial Payments',
  PAYLINKS_RECURRING_CHECKBOX: 'Paylinks — save cards for future transactions',
  SETTLEMENT_TYPE: 'Payment Capture',
  SHOPPER_CREDENTIAL_ACCESS: 'Payment Credential Sharing',
  SPLIT_PAYMENTS: 'Split Payments',
  SURCHARGE_FEE: 'Surcharge fee',
  SUPPORTED_COUNTRIES: 'Cross Border Payments',
  TERMS_LINKS: 'Shopper consent links',
  TIPPING_PERCENTAGES: 'Tipping',
};

const CALLBACK_DISPLAY_NAMES: Record<CallbackType, string> = {
  Products: 'Products callback',
  ShippingAndTax: 'Shipping and tax callback',
  Fulfillability: 'Fulfillability callback',
  ShopperCredentialSharing: 'Payment Credential Sharing',
  AgenticCheckoutValidateTotal: 'Agentic Checkout validate total callback',
};

function formatSettingNameFallback(name: string): string {
  return name
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getConfigDisplayLabel(name: ConfigResponse['name']): string {
  return CONFIG_DISPLAY_NAMES[name] ?? formatSettingNameFallback(name);
}

export function getCallbackDisplayLabel(callbackType: CallbackType): string {
  return CALLBACK_DISPLAY_NAMES[callbackType] ?? formatSettingNameFallback(callbackType);
}

export function uniqueDisplayLabels(labels: string[]): string[] {
  return labels.filter((label, index) => labels.indexOf(label) === index);
}

function formatLabelsList(labels: string[]): string {
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export function inheritableLabelsFromConfigNames(names: ConfigResponse['name'][]): string[] {
  return uniqueDisplayLabels(
    names.filter(isInheritableConfigName).map((name) => getConfigDisplayLabel(name)),
  );
}

export type ParentHierarchySaveBarCopy = {
  text: string;
  detailText?: string;
};

/** Save bar primary + detail copy for parent merchants with inheritable pending changes. */
export function getParentHierarchySaveBarCopy(
  inheritableSettingLabels: string[],
  options: { isSaving: boolean; hasDescendants: boolean },
): ParentHierarchySaveBarCopy {
  if (options.isSaving) {
    return { text: 'Saving current changes...' };
  }

  if (!options.hasDescendants || inheritableSettingLabels.length === 0) {
    return { text: 'Save current changes?' };
  }

  const multiple = inheritableSettingLabels.length > 1;
  const detailText = multiple
    ? `The changes to ${formatLabelsList(inheritableSettingLabels)} may apply to child merchants.`
    : `Changes to the ${inheritableSettingLabels[0]} setting may apply to child merchants.`;

  return {
    text: 'Save current changes?',
    detailText,
  };
}
